/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useToast } from '@facephi/ui-react';
import axios from 'axios';
import jwt, { Jwt, JwtPayload } from 'jsonwebtoken';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  RequestMethods,
  useOperationId,
  useVariables,
  useLogger,
  useTrack,
} from '../hooks';
import { useLog } from '../providers';
import { Storage, endPoints, AuthName, RoutesName } from '../states';
import { livenessValidationState, termsAcceptedState } from '../states/atoms';
import {
  ClaimsList,
  ClaimsType,
  CLAIMS_TOKEN,
  LoginDto,
  TokenGrantType,
} from '../states/model';

type IProps = {
  children: React.ReactNode;
};

type ContextProps = {
  logged: boolean;
  logout(): void;
  terms: boolean;
  loadingTerms: boolean;
  termsText?: string;
  handleLogin(user: LoginDto): void;
  acceptTerms(): void;
  checkTermsAccepted(): void;
  handleRefreshToken(): Promise<boolean>;
  refreshToken: string | null;
  error: string | null;
  token: string | null;
  tokenValid: boolean;
  email?: string;
  canDisableLiveness?: boolean;
  hasAntifraud: boolean;
  hasPad: boolean;
  tenantDefault?: string;
  demoVersion: string;
  initError: boolean;
  advancedTracking?: boolean;
  claims?: ClaimsType;
  canActivateActiveLiveness?: boolean;
  canActivateCustomerEvaluation?: boolean;
  detailedScreen?: boolean | null;
};

const Ctx = createContext<ContextProps>({
  logged: false,
  terms: false,
  loadingTerms: true,
  termsText: '',
  logout: () => {},
  handleLogin: () => {},
  acceptTerms: () => {},
  checkTermsAccepted: () => {},
  handleRefreshToken: () => new Promise((resolve) => resolve(true)),
  error: null,
  token: null,
  tokenValid: false,
  refreshToken: null,
  canDisableLiveness: false,
  hasAntifraud: false,
  hasPad: false,
  demoVersion: '',
  initError: false,
  advancedTracking: false,
  claims: {},
  canActivateActiveLiveness: false,
  canActivateCustomerEvaluation: false,
  detailedScreen: false,
});

export const AuthProvider = ({ children }: IProps) => {
  const { clearStorage } = useOperationId();
  const {
    appApiId,
    apiDemoUrl,
    wiremockUrl,
    appTesting,
    tenantId: tenantEnvironment,
    isCanada,
  } = useVariables();
  const { toastManager } = useToast();
  const { t } = useTranslation();
  const { setInspectletTag } = useLog();
  const [tenantDefault, setTenantDefault] = useState<string>();

  const [logged, setLogged] = useState<boolean>(
    localStorage.getItem(Storage.token) ? true : false
  );
  const [terms, setTerms] = useRecoilState(termsAcceptedState);
  const setLivenessValidation = useSetRecoilState(livenessValidationState);
  const [loadingTerms, setLoadingTerms] = useState<boolean>(true);
  const [termsText, setTermsText] = useState<string>();
  const [claims, setClaims] = useState<ClaimsType>();
  const [advancedTracking, setAdvancedTracking] = useState<boolean>(false);
  const [canActivateActiveLiveness, setCanActivateActiveLiveness] =
    useState<boolean>(false);
  const [canActivateCustomerEvaluation, setCanActivateCustomerEvaluation] =
    useState<boolean>(false);

  const [token, setToken] = useState<string | null>(
    localStorage.getItem(Storage.token)
  );
  const [refreshToken, setRefreshToken] = useState<string | null>(
    localStorage.getItem(Storage.refreshToken)
  );

  const [error, setError] = useState<string | null>(null);

  const [demoVersion, setDemoVersion] = useState<string>('');
  const [initError, setInitError] = useState<boolean>(false);
  const [detailedScreen, setDetailedScreen] = useState<boolean | null>(null);

  const { captureException, captureMessage, setUser } = useLogger();

  const { hasFeatureFlag, getFeaturePayload } = useTrack();

  const isLogged = (token: string, refreshToken: string) => {
    localStorage.setItem(Storage.token, token);
    localStorage.setItem(Storage.refreshToken, refreshToken);
    setLogged(true);
  };

  const logout = () => {
    localStorage.removeItem(Storage.token);
    localStorage.removeItem(Storage.refreshToken);
    clearStorage();
    setLogged(false);
    setTenantDefault(undefined);
    window.location.replace(RoutesName.login);
  };

  const checkTermsAccepted = () => {
    setLoadingTerms(true);
    axios({
      method: RequestMethods.get,
      url: `${apiDemoUrl}${endPoints.TermsAcceptance}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AuthName.token)}`,
        'x-demo-app-version': demoVersion,
      },
      params: {
        userId: claims?.[ClaimsList.email],
      },
    })
      .then((response) => {
        const { userId } = response.data;
        if (userId && logged) {
          setTerms(true);
        }
      })
      .catch(() => {
        setTerms(false);
      })
      .finally(() => setLoadingTerms(false));
  };

  const acceptTerms = () => {
    axios({
      method: RequestMethods.post,
      url: `${apiDemoUrl}${endPoints.TermsAcceptance}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AuthName.token)}`,
        'x-demo-app-version': '1',
      },
      data: {
        userId: claims?.[ClaimsList.email],
      },
    })
      .then((response) => {
        const { userId } = response.data;
        setTerms(true);
        toastManager({
          duration: 3500,
          type: 'success',
          message: `${t('T&C accepted successfully')}: ${userId}`,
          testId: 'toast-accepted-ok',
        });
      })
      .catch((error) => {
        captureException(error as Error, { operation: 'acceptingTAC' });
        toastManager({
          duration: 3500,
          type: 'error',
          message: t('Something went wrong with the acceptance of T&C'),
          testId: 'toast-accepted-ko',
        });
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getClaimsFromPosthog = (user: string) => {
    if (hasFeatureFlag('auth')) {
      const claims: any[] = getFeaturePayload('auth') as any[];
      return claims.filter((element) => element.user === user);
    }
  };

  const getClaims = (tokenInfo: JwtPayload) =>
    Object.entries(tokenInfo).reduce((acc, [key, value]) => {
      if (key.includes(CLAIMS_TOKEN)) {
        acc[key.replace(CLAIMS_TOKEN, '')] = value;
      }
      return acc;
    }, {} as ClaimsType);

  const handleRefreshToken = (): Promise<boolean> =>
    new Promise((resolve, reject) => {
      axios
        .post(`${apiDemoUrl}/auth/token`, {
          application_api_id: appApiId,
          grant_type: TokenGrantType.RefreshToken,
          refresh_token: refreshToken,
        })
        .then((response) => {
          if (
            !!localStorage.getItem(Storage.token) &&
            !!localStorage.getItem(Storage.refreshToken)
          ) {
            setToken(response.data.access_token);
            setRefreshToken(response.data.refresh_token);

            isLogged(response.data.access_token, response.data.refresh_token);
            resolve(true);
          }
        })
        .catch(() => {
          reject(false);
        });
    });

  const handleLogin = async (user: LoginDto) => {
    try {
      const url = wiremockUrl
        ? `${wiremockUrl}/auth`
        : `${apiDemoUrl}/auth/token`;
      const response = await axios
        .post(url, {
          application_api_id: appApiId,
          grant_type: TokenGrantType.PasswordRealm,
          ...user,
        })
        .then((response) => {
          return response.data;
        });
      setToken(response.access_token);
      setRefreshToken(response.refresh_token);

      isLogged(response.access_token, response.refresh_token);
    } catch (error: any) {
      setError('error');
      captureMessage('Error in login');
      captureException(error);
    }
  };

  const tokenValid = useMemo(() => {
    if (token) {
      const decodeToken: Jwt | null = jwt.decode(token, { complete: true });
      const dateNow = new Date();
      return (
        ((decodeToken?.payload as JwtPayload)?.exp || 0) * 1000 >
        dateNow.getTime()
      );
    }

    return false;
  }, [token]);

  useEffect(() => {
    setError(null);
  }, [logged]);

  useEffect(() => {
    if (token) {
      const tokenInfo: JwtPayload = jwt.decode(token) as JwtPayload;

      const claims = getClaims(tokenInfo);

      if (isCanada) {
        setTenantDefault(tenantEnvironment);
      } else if (claims.tenant) {
        if (claims[ClaimsList.tenant] instanceof Array) {
          // TODO setTenantDefault(claims[ClaimsList.tenant][0] as string);
          setTenantDefault('8d04089d-8273-442e-ad40-2bf10ff494b3');
        }
      } else {
        // TO DO setTenantDefault(tenantEnvironment);
        setTenantDefault('8d04089d-8273-442e-ad40-2bf10ff494b3');
      }

      setClaims(claims);
      setInspectletTag(['identify', claims[ClaimsList.email]]);
      setInspectletTag(['tagSession', { email: claims[ClaimsList.email] }]);
      setUser({
        email: claims[ClaimsList.email],
      });
    }
  }, [token]);

  useEffect(() => {
    if (!demoVersion && logged && !wiremockUrl) {
      const latest = '1';
      setDemoVersion(latest);
    }
    return () => setInitError(false);
  }, [logged]);

  useEffect(() => {
    if (claims) {
      axios({
        method: RequestMethods.get,
        url: `${apiDemoUrl}${endPoints.Terms}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(AuthName.token)}`,
          'x-demo-app-version': '1',
        },
      })
        .then((response) => {
          const { content } = response.data;
          setTermsText(content);
        })
        .catch((error) => {
          captureException(error as Error, { operation: 'fetchingTAC' });
          if (appTesting) {
            toastManager({
              duration: 3500,
              type: 'error',
              canClose: true,
              message: t('Error while trying to retrieve TAC'),
              testId: 'toast-get-accepted-ko',
            });
          }
        });
    }
  }, [claims]);

  useEffect(() => {
    if (termsText && claims?.[ClaimsList.email]) {
      checkTermsAccepted();
    }
  }, [termsText, claims]);

  useEffect(() => {
    if (appTesting) {
      setLivenessValidation(false);
      setAdvancedTracking(false);
      setCanActivateActiveLiveness(false);
      setCanActivateCustomerEvaluation(false);
    } else {
      if (!claims?.[ClaimsList.liveness]) {
        setLivenessValidation(!claims?.[ClaimsList.liveness]);
      }
      if (claims?.[ClaimsList.advancedTracking]) {
        setAdvancedTracking(true);
      }
      if (claims?.[ClaimsList.activeLiveness]) {
        setCanActivateActiveLiveness(true);
      }
      if (claims?.[ClaimsList.customerEvaluation]) {
        setCanActivateCustomerEvaluation(true);
      }
      if (typeof claims?.[ClaimsList.detailedScreen] !== undefined) {
        setDetailedScreen(claims?.[ClaimsList.detailedScreen]);
      }
    }
  }, [claims]);

  return (
    <Ctx.Provider
      value={{
        logged,
        logout,
        handleLogin,
        terms,
        loadingTerms,
        termsText,
        error,
        handleRefreshToken,
        token,
        tokenValid,
        email: '' + claims?.[ClaimsList.email],
        acceptTerms,
        refreshToken,
        canDisableLiveness: !!claims?.[ClaimsList.liveness],
        hasAntifraud: !!claims?.[ClaimsList.antifraud],
        hasPad: !!claims?.[ClaimsList.pad],
        tenantDefault,
        demoVersion,
        initError,
        checkTermsAccepted,
        advancedTracking,
        claims,
        canActivateActiveLiveness,
        canActivateCustomerEvaluation,
        detailedScreen,
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = () => useContext(Ctx);
